/**
 * ARIA Accessibility Helpers
 *
 * Pure functions for managing ARIA attributes on grid elements.
 * Implements caching to avoid redundant DOM writes during scroll.
 *
 * @module internal/aria
 */

import { DEFAULT_A11Y_MESSAGES } from '../defaults';
import type { A11yMessages, GridConfig } from '../types';

// #region Types

/**
 * State for caching ARIA attributes to avoid redundant DOM writes.
 */
export interface AriaState {
  /** Last set row count */
  rowCount: number;
  /** Last set column count */
  colCount: number;
  /** Last set aria-label */
  ariaLabel: string | undefined;
  /** Last set aria-labelledby */
  ariaLabelledBy: string | undefined;
  /** Last set aria-describedby */
  ariaDescribedBy: string | undefined;
  /** Last set aria-roledescription */
  ariaRoleDescription: string | undefined;
  /** Last source row count announced via `dataLoaded`; used to suppress duplicate announcements */
  lastAnnouncedSourceCount: number;
}

/**
 * Create initial ARIA state.
 */
export function createAriaState(): AriaState {
  return {
    rowCount: -1,
    colCount: -1,
    ariaLabel: undefined,
    ariaLabelledBy: undefined,
    ariaDescribedBy: undefined,
    ariaRoleDescription: undefined,
    lastAnnouncedSourceCount: -1,
  };
}

// #endregion

// #region Count Updates

/**
 * Update ARIA row and column counts on grid elements.
 * Uses caching to avoid redundant DOM writes on every scroll frame.
 *
 * @param state - ARIA state for caching
 * @param rowsBodyEl - Element to set aria-rowcount/aria-colcount on
 * @param bodyEl - Element to set role="rowgroup" on
 * @param rowCount - Current row count
 * @param colCount - Current column count
 * @returns true if anything was updated
 */
export function updateAriaCounts(
  state: AriaState,
  rowsBodyEl: HTMLElement | null,
  bodyEl: HTMLElement | null,
  rowCount: number,
  colCount: number,
): boolean {
  // Skip if nothing changed (hot path optimization for scroll)
  if (rowCount === state.rowCount && colCount === state.colCount) {
    return false;
  }

  const prevRowCount = state.rowCount;
  state.rowCount = rowCount;
  state.colCount = colCount;

  // Update ARIA counts on inner grid element
  if (rowsBodyEl) {
    rowsBodyEl.setAttribute('aria-rowcount', String(rowCount));
    rowsBodyEl.setAttribute('aria-colcount', String(colCount));
  }

  // Set role="rowgroup" on .rows only when there are rows (ARIA compliance)
  if (rowCount !== prevRowCount && bodyEl) {
    if (rowCount > 0) {
      bodyEl.setAttribute('role', 'rowgroup');
    } else {
      bodyEl.removeAttribute('role');
    }
  }

  return true;
}

// #endregion

// #region Label Updates

/**
 * Determine the effective aria-label for the grid.
 * Priority: explicit config > shell title > nothing
 *
 * The shell title is read from `config.shell.header.title`. The shell plugin
 * folds any light-DOM `<tbw-grid-header title>` into that field via its
 * `processConfig` hook (extraction #370), so core needs no plugin state here.
 *
 * @param config - Grid configuration (the merged effective config)
 * @returns The aria-label to use, or undefined
 */
export function getEffectiveAriaLabel<T>(config: GridConfig<T> | undefined): string | undefined {
  const explicitLabel = config?.gridAriaLabel;
  if (explicitLabel) return explicitLabel;

  return config?.shell?.header?.title ?? undefined;
}

/**
 * Update ARIA label and describedby attributes on the grid container.
 * Uses caching to avoid redundant DOM writes.
 *
 * @param state - ARIA state for caching
 * @param rowsBodyEl - Element to set aria-label/aria-describedby on
 * @param config - Grid configuration (the merged effective config)
 * @returns true if anything was updated
 */
export function updateAriaLabels<T>(
  state: AriaState,
  rowsBodyEl: HTMLElement | null,
  config: GridConfig<T> | undefined,
): boolean {
  if (!rowsBodyEl) return false;

  let updated = false;

  // aria-labelledby wins per WAI-ARIA accessible-name precedence — when set,
  // `aria-label` is suppressed even if explicitly configured to avoid two
  // competing names being computed by AT.
  const ariaLabelledBy = config?.gridAriaLabelledBy;
  const effectiveLabel = ariaLabelledBy ? undefined : getEffectiveAriaLabel(config);

  // Update aria-labelledby only if changed
  if (ariaLabelledBy !== state.ariaLabelledBy) {
    state.ariaLabelledBy = ariaLabelledBy;
    if (ariaLabelledBy) {
      rowsBodyEl.setAttribute('aria-labelledby', ariaLabelledBy);
    } else {
      rowsBodyEl.removeAttribute('aria-labelledby');
    }
    updated = true;
  }

  // Update aria-label only if changed
  if (effectiveLabel !== state.ariaLabel) {
    state.ariaLabel = effectiveLabel;
    if (effectiveLabel) {
      rowsBodyEl.setAttribute('aria-label', effectiveLabel);
    } else {
      rowsBodyEl.removeAttribute('aria-label');
    }
    updated = true;
  }

  // Update aria-describedby only if changed
  const ariaDescribedBy = config?.gridAriaDescribedBy;
  if (ariaDescribedBy !== state.ariaDescribedBy) {
    state.ariaDescribedBy = ariaDescribedBy;
    if (ariaDescribedBy) {
      rowsBodyEl.setAttribute('aria-describedby', ariaDescribedBy);
    } else {
      rowsBodyEl.removeAttribute('aria-describedby');
    }
    updated = true;
  }

  // Update aria-roledescription only if changed. Apps can override the
  // announced role name (e.g. localized "Tabell" instead of generic "grid").
  const ariaRoleDescription = config?.gridAriaRoleDescription;
  if (ariaRoleDescription !== state.ariaRoleDescription) {
    state.ariaRoleDescription = ariaRoleDescription;
    if (ariaRoleDescription) {
      rowsBodyEl.setAttribute('aria-roledescription', ariaRoleDescription);
    } else {
      rowsBodyEl.removeAttribute('aria-roledescription');
    }
    updated = true;
  }

  return updated;
}

// #endregion

// #region Live Announcements

/**
 * Announce a message to screen readers via the grid's aria-live region.
 * Clears then sets text to ensure repeated messages are re-announced.
 *
 * Respects `effectiveConfig.a11y.announcements` — if set to `false`,
 * the announcement is silently skipped.
 *
 * @param gridEl - The grid host element (or any ancestor containing `.tbw-sr-only`)
 * @param message - The message to announce
 */
export function announce(gridEl: HTMLElement, message: string): void {
  // Check if announcements are disabled via config.
  // gridEl is always a DataGridElement at runtime — use property access safely.
  if (!gridEl) return;
  const config =
    'effectiveConfig' in gridEl
      ? (gridEl as HTMLElement & { effectiveConfig?: GridConfig }).effectiveConfig
      : undefined;
  if (config?.a11y?.announcements === false) return;

  const el = gridEl.querySelector?.('.tbw-sr-only');
  if (!el) return;
  // Clear first so identical consecutive messages are still announced
  el.textContent = '';
  // `requestAnimationFrame` can be undefined if a debounced announcement fires
  // after the DOM/host environment is torn down (notably in unit tests). Fall
  // back to a direct assignment so `announce` never throws out of band.
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      el.textContent = message;
    });
  } else {
    el.textContent = message;
  }
}

/**
 * Get an announcement message, preferring custom overrides from config.
 * Falls back to default English messages when no override is provided.
 *
 * @param gridEl - The grid host element
 * @param key - The message key from A11yMessages
 * @param args - Arguments to pass to the message function
 */
export function getA11yMessage<K extends keyof A11yMessages>(
  gridEl: HTMLElement,
  key: K,
  ...args: Parameters<Required<A11yMessages>[K]>
): string {
  type MessageFn = (...a: Parameters<Required<A11yMessages>[K]>) => string;
  const config =
    gridEl && 'effectiveConfig' in gridEl
      ? (gridEl as HTMLElement & { effectiveConfig?: GridConfig }).effectiveConfig
      : undefined;
  const customFn = config?.a11y?.messages?.[key] as MessageFn | undefined;
  if (customFn) return customFn(...args);
  return (DEFAULT_A11Y_MESSAGES[key] as MessageFn)(...args);
}

/**
 * Announce a `dataLoaded` message — but only when the source row count has
 * actually changed since the last announcement. This prevents announcement
 * spam from internal `data-change` emits triggered by sort/filter/edit, which
 * have their own dedicated announcements.
 *
 * @param gridEl - The grid host element
 * @param state - ARIA state (tracks last announced count)
 * @param sourceRowCount - Current source row count
 */
export function announceDataLoaded(gridEl: HTMLElement, state: AriaState, sourceRowCount: number): void {
  if (sourceRowCount === state.lastAnnouncedSourceCount) return;
  const previousAnnouncedSourceCount = state.lastAnnouncedSourceCount;
  state.lastAnnouncedSourceCount = sourceRowCount;
  // Skip only the initial sentinel → 0 transition (empty grid mount). A later
  // transition to 0 (e.g. data cleared / reloaded to an empty result set) should
  // still announce so screen-reader users hear that the grid is now empty.
  if (sourceRowCount === 0 && previousAnnouncedSourceCount < 0) return;
  announce(gridEl, getA11yMessage(gridEl, 'dataLoaded', sourceRowCount));
}

// #endregion
